import { Component, inject, input, output, signal, OnChanges, SimpleChanges, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { ButtonDirective } from '../../../../components/button/button.directive';
import { InputComponent } from '../../../../components/input/input.component';
import { SelectComponent } from '../../../../components/select/select.component';
import { ModalComponent } from '../../../../components/modal/modal.component';
import { DictionaryService } from '../../../../services/dictionary.service';
import { FilterOption } from '../../../../components/table/table.interface';
import { environment } from '../../../../../environments/environment';
import { SnackbarService } from '../../../../services/snackbar.service';
import { CustomerService } from '../../../../services/customer.service';
import { CustomerDropdownCacheService } from '../../../../services/customer-dropdown-cache.service';

export interface CustomerData {
    customerId: string;
    name: string;
    email?: string;
    phone?: string;
    class: string;
    mktClass: string;
    specialClass: string;
    speciality?: string;
    type: string;
    center?: string;
    country: string;
    province: string;
    city: string;
    targetedFrequency: number;
    isDeleted?: boolean;
}

@Component({
    selector: 'app-customer-edit-modal',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ButtonDirective,
        InputComponent,
        SelectComponent,
        ModalComponent,
    ],
    templateUrl: './customer-edit-modal.component.html',
    styleUrls: ['./customer-edit-modal.component.scss'],
})
export class CustomerEditModalComponent implements OnChanges {
    protected dictionaryService = inject(DictionaryService);
    private http = inject(HttpClient);
    private snackbarService = inject(SnackbarService);
    private customerService = inject(CustomerService);
    protected dropdownCache = inject(CustomerDropdownCacheService);
    private baseUrl = environment.apiBaseUrl;
    private readonly emailPattern = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

    // Inputs
    isOpen = input<boolean>(false);
    customerData = input<CustomerData | null>(null);

    // Outputs
    onClose = output<void>();
    onSave = output<void>();

    // Modal open state
    modalOpened = signal<boolean>(false);

    // Loading states
    isSubmitting = signal<boolean>(false);

    // City options derived reactively from selected province + cached provinces data
    cityOptions = computed(() => this.dropdownCache.getCityOptions(this.selectedProvince()));
    typeOptions = signal<string[]>(['Doctor', 'Provider', 'Pharmacy', 'Hospital']);

    isDropdownDataReady = this.dropdownCache.isReady;

    private formDataApplied = signal(false);

    // Selected values
    selectedCountry = signal<string>('');
    selectedProvince = signal<string>('');
    selectedCity = signal<string>('');
    selectedClassType = signal<string>('');
    selectedMktClass = signal<string>('');
    selectedSpecialClass = signal<string>('');
    selectedSpeciality = signal<string>('');
    selectedType = signal<string>('');

    // Computed
    isProvinceDisabled = computed(() => !this.selectedCountry());
    isCityDisabled = computed(() => !this.selectedProvince());

    // Get translated customer type
    customerTypeDisplay = computed(() => {
        const type = this.customerData()?.type;
        if (!type) return '';

        // Capitalize first letter and translate
        const capitalizedType = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
        return this.dictionaryService.get(capitalizedType) || capitalizedType;
    });

    // Form
    protected editForm = new FormGroup({
        name: new FormControl<string>(''),
        email: new FormControl<string>(''),
        phone: new FormControl<string>(''),
        center: new FormControl<string>(''),
        targetedFrequency: new FormControl<number | null>(null),
    });

    constructor() {
        // Apply form data only after dropdown data is ready so speciality abbreviation
        // lookup works even when the cache is cold on first open.
        effect(() => {
            if (this.isOpen() && this.isDropdownDataReady() && !this.formDataApplied()) {
                this.loadFormData();
                this.formDataApplied.set(true);
            }
            if (!this.isOpen() && this.formDataApplied()) {
                this.formDataApplied.set(false);
            }
        }, { allowSignalWrites: true });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isOpen']) {
            this.modalOpened.set(this.isOpen());
            if (this.isOpen()) {
                this.dropdownCache.loadAllOptions();
            }
        }
    }

    private loadFormData(): void {
        const data = this.customerData();
        if (!data) return;

        // Set form values
        this.editForm.patchValue({
            name: data.name,
            email: data.email || '',
            phone: data.phone || '',
            center: data.center || '',
            targetedFrequency: data.targetedFrequency,
        });

        // Set selected dropdown values
        this.selectedCountry.set(data.country);
        this.selectedProvince.set(data.province);
        this.selectedCity.set(data.city);
        this.selectedClassType.set(data.class);
        this.selectedMktClass.set(data.mktClass);
        this.selectedSpecialClass.set(data.specialClass);
        const specialitiesData = this.dropdownCache.specialitiesData();
        const matchedSpeciality = specialitiesData.find(
          (s) => s.speciality === data.speciality,
        );
        this.selectedSpeciality.set(
          matchedSpeciality?.abbreviation || data.speciality || '',
        );
        // Capitalize type for selection (e.g., 'doctor' -> 'Doctor')
        const capitalizedType = data.type.charAt(0).toUpperCase() + data.type.slice(1).toLowerCase();
        this.selectedType.set(capitalizedType);

    }

    onCountryChange(option: FilterOption): void {
        this.selectedCountry.set(option.value);
        this.selectedProvince.set('');
        this.selectedCity.set('');
    }

    onProvinceChange(option: FilterOption): void {
        this.selectedProvince.set(option.value);
        this.selectedCity.set('');
    }

    onCityChange(option: FilterOption): void {
        this.selectedCity.set(option.value);
    }

    onClassTypeChange(option: FilterOption): void {
        this.selectedClassType.set(option.value);
    }

    onMktClassChange(option: FilterOption): void {
        this.selectedMktClass.set(option.value);
    }

    onSpecialClassChange(option: FilterOption): void {
        this.selectedSpecialClass.set(option.value);
    }

    onSpecialityChange(option: FilterOption): void {
        this.selectedSpeciality.set(option.value);
    }

    onTypeChange(option: FilterOption): void {
        this.selectedType.set(option.value);
    }

    handleModalClose(): void {
        this.modalOpened.set(false);
        this.onClose.emit();
    }

    /**
     * Check if target frequency has changed
     */
    private isTargetedFrequencyChanged(): boolean {
        const data = this.customerData();
        if (!data) return false;
        const currentTargetedFrequency = Number(this.editForm.controls.targetedFrequency.value);
        return currentTargetedFrequency !== data.targetedFrequency;
    }

    private isChangedTargetedFrequencyValid(): boolean {
        return !this.isTargetedFrequencyInvalid();
    }

    isTargetedFrequencyInvalid(): boolean {
        if (!this.isTargetedFrequencyChanged()) return false;

        const currentTargetedFrequency = Number(this.editForm.controls.targetedFrequency.value);
        return !Number.isFinite(currentTargetedFrequency) || currentTargetedFrequency < 0;
    }

    private isEmailChanged(): boolean {
        const data = this.customerData();
        if (!data) return false;

        const currentEmail = this.editForm.controls.email.value ?? '';
        return currentEmail !== (data.email ?? '');
    }

    private isChangedEmailValid(): boolean {
        return !this.isEmailInvalid();
    }

    isEmailInvalid(): boolean {
        if (!this.isEmailChanged()) return false;

        const currentEmail = this.editForm.controls.email.value ?? '';
        return !!currentEmail && !this.emailPattern.test(currentEmail);
    }

    /**
     * Build and return payload with only non-frequency changed fields
     * Returns null if no fields (other than frequency) have changed
     */
    private buildChangedFieldsPayload(): any | null {
        const data = this.customerData();
        if (!data) return null;

        const updatePayload: any = {};

        // Compare and add only changed fields
        const currentName = this.editForm.controls.name.value || '';
        if (currentName !== data.name) {
            updatePayload.name = currentName;
        }

        const currentEmail = this.editForm.controls.email.value ?? '';
        if (currentEmail !== (data.email ?? '')) {
            updatePayload.email = currentEmail;
        }

        const currentPhone = this.editForm.controls.phone.value ?? '';
        if (currentPhone !== (data.phone ?? '')) {
            updatePayload.phone = currentPhone;
        }

        // Check location changes
        const currentCountry = this.selectedCountry();
        const currentProvince = this.selectedProvince();
        const currentCity = this.selectedCity();
        if (currentCountry !== data.country || currentProvince !== data.province || currentCity !== data.city) {
            updatePayload.location = {
                country: currentCountry,
                province: currentProvince,
                city: currentCity,
            };
        }

        const currentClassType = this.selectedClassType();
        if (currentClassType !== data.class) {
            updatePayload.classType = currentClassType;
        }

        const currentMktClass = this.selectedMktClass();
        if (currentMktClass !== data.mktClass) {
            updatePayload.mktClass = currentMktClass;
        }

        const currentSpecialClass = this.selectedSpecialClass();
        if (currentSpecialClass !== data.specialClass) {
            updatePayload.specialClass = currentSpecialClass;
        }

        // Capitalize type for comparison
        const capitalizedOriginalType = data.type.charAt(0).toUpperCase() + data.type.slice(1).toLowerCase();
        const currentType = this.selectedType();
        if (currentType !== capitalizedOriginalType) {
            updatePayload.type = currentType;
        }

        const currentSpeciality = this.selectedSpeciality();
        if (currentSpeciality !== (data.speciality || '')) {
            updatePayload.speciality = currentSpeciality;
        }

        const currentCenter = this.editForm.controls.center.value || '';
        if (currentCenter !== (data.center || '')) {
            updatePayload.center = currentCenter;
        }

        // Return payload only if there are changes
        return Object.keys(updatePayload).length > 0 ? updatePayload : null;
    }

    /**
     * Build frequency-only payload for editing draft
     * Only sends id and targetedFrequency as required by the API
     */
    private buildFrequencyDraftPayload(): any {
        const data = this.customerData();
        if (!data) return null;

        const currentTargetedFrequency = Number(this.editForm.controls.targetedFrequency.value);
        
        return {
            id: data.customerId,
            targetedFrequency: currentTargetedFrequency,
        };
    }

    /**
     * Make API call to update changed fields directly (non-frequency changes)
     */
    private async updateChangedFieldsDirectly(payload: any): Promise<void> {
        const data = this.customerData();
        if (!data) return;

        const response = await lastValueFrom(
            this.http.put<any>(`${this.baseUrl}/customers/${data.customerId}`, payload)
        );

        if (!response?.success) {
            throw new Error(response?.message || 'Failed to update customer');
        }
    }

    /**
     * Make API call to update target frequency via editing draft
     */
    private async updateTargetedFrequencyDraft(payload: any): Promise<void> {
        const response = await lastValueFrom(
            this.http.post<any>(`${this.baseUrl}/customer-drafts/editing-drafts`, { customers: [payload] })
        );

        if (response?.success) {
            // Trigger refresh of Editing draft tab
            this.customerService.triggerDraftTableRefresh('Editing');
        } else {
            throw new Error(response?.message || 'Failed to create editing draft');
        }
    }

    async submit(): Promise<void> {
        if (!this.isFormValid()) {
            return;
        }

        const data = this.customerData();
        if (!data) return;

        this.isSubmitting.set(true);

        try {
            // Get flags for what changed
            const changedFieldsPayload = this.buildChangedFieldsPayload();
            const isFrequencyChanged = this.isTargetedFrequencyChanged();

            // Case 1: No changes detected
            if (!changedFieldsPayload && !isFrequencyChanged) {
                this.snackbarService.showWarning('No changes detected');
                this.isSubmitting.set(false);
                return;
            }

            // Case 2: Only changed fields (no frequency change) - Make 1 API call
            if (changedFieldsPayload && !isFrequencyChanged) {
                await this.updateChangedFieldsDirectly(changedFieldsPayload);
                this.snackbarService.showByCode('S0706001');
                this.onSave.emit();
                this.handleModalClose();
                return;
            }

            // Case 3: Only frequency changed - Make 1 API call
            if (!changedFieldsPayload && isFrequencyChanged) {
                const frequencyPayload = this.buildFrequencyDraftPayload();
                await this.updateTargetedFrequencyDraft(frequencyPayload);
                this.snackbarService.showByCode('S0802001');
                this.onSave.emit();
                this.handleModalClose();
                return;
            }

            // Case 4: Both changed fields AND frequency changed - Make both API calls in parallel
            const frequencyPayload = this.buildFrequencyDraftPayload();
            await Promise.all([
                this.updateChangedFieldsDirectly(changedFieldsPayload),
                this.updateTargetedFrequencyDraft(frequencyPayload),
            ]);

            this.snackbarService.showByCode('S0706001');
            this.onSave.emit();
            this.handleModalClose();

        } catch (error: any) {
            console.error('Error during submit:', error);
            if (error.error?.messageCode) {
                this.snackbarService.showByCode(error.error.messageCode);
            } else if (error.message) {
                this.snackbarService.showError(error.message);
            } else {
                this.snackbarService.showError(error.error?.message || 'Failed to save changes');
            }
        } finally {
            this.isSubmitting.set(false);
        }
    }

    isFormValid(): boolean {
        const hasApiCall = !!this.buildChangedFieldsPayload() || this.isTargetedFrequencyChanged();
        return hasApiCall && this.isChangedEmailValid() && this.isChangedTargetedFrequencyValid();
    }
}
